import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Chip, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { buscarArticulos, ARTICULOS, ROLES_MANUAL, rolVisibleParaUsuario } from '../data/ayuda/articulos';

const ROL_ACTUAL = () => sessionStorage.getItem('rol') || 'recepcionista';

// Categorías que tienen su propio manual PDF descargable, y qué "rol de sistema"
// hay que usar para filtrar el contenido (la categoría 'administrador' del manual
// corresponde al rol de sistema 'admin').
const ROL_SISTEMA_PARA_PDF = {
  recepcionista: 'recepcionista',
  medico: 'medico',
  administrador: 'admin',
};

export default function ManualUsuario() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rolFiltro = searchParams.get('rol') || null;
  const [query, setQuery] = useState('');
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const rolUsuario = ROL_ACTUAL();
  const nombreUsuario = sessionStorage.getItem('nombre') || '';

  const rolesVisibles = ROLES_MANUAL.filter(r => rolVisibleParaUsuario(r.id, rolUsuario));

  const resultadosBusqueda = useMemo(() => {
    if (!query.trim()) return null;
    return buscarArticulos(query, rolUsuario);
  }, [query, rolUsuario]);

  const articulosRol = useMemo(() => {
    if (!rolFiltro) return null;
    return ARTICULOS.filter(a => a.rol === rolFiltro && a.roles.includes(rolUsuario));
  }, [rolFiltro, rolUsuario]);

  const rolSeleccionado = ROLES_MANUAL.find(r => r.id === rolFiltro);

  function seleccionarRol(id) {
    setQuery('');
    setSearchParams({ rol: id });
  }

  function limpiarFiltro() {
    setQuery('');
    setSearchParams({});
  }

  async function descargarPdf() {
    const rolSistema = ROL_SISTEMA_PARA_PDF[rolFiltro];
    if (!rolSistema) return;
    setGenerandoPdf(true);
    try {
      const { generarManualPDF } = await import('../pdf/manual/generarManualPDF');
      const blob = await generarManualPDF(rolSistema, rolSeleccionado.label, nombreUsuario);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `manual-${rolFiltro}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setGenerandoPdf(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 680 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 0.5 }}>
          📖 Manual de Usuario
        </Typography>
        <Typography sx={{ color: '#90a4ae', fontSize: 14 }}>
          Guías y procedimientos para usar Gestar.
        </Typography>
      </Box>

      {/* Buscador */}
      <TextField
        fullWidth
        placeholder="¿Qué necesita hacer?"
        value={query}
        onChange={e => { setQuery(e.target.value); setSearchParams({}); }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#546e7a', fontSize: 20 }} />
            </InputAdornment>
          ),
          style: { color: '#e0e0e0' },
        }}
        sx={{
          mb: 4,
          '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
          '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
        }}
      />

      {/* Resultados de búsqueda */}
      {resultadosBusqueda && (
        <Box>
          <Typography sx={{ fontSize: 13, color: '#546e7a', mb: 2 }}>
            {resultadosBusqueda.length === 0
              ? 'No se encontraron artículos.'
              : `${resultadosBusqueda.length} resultado${resultadosBusqueda.length !== 1 ? 's' : ''}`}
          </Typography>
          <ListaArticulos articulos={resultadosBusqueda} navigate={navigate} />
        </Box>
      )}

      {/* Vista de rol seleccionado */}
      {!resultadosBusqueda && rolFiltro && articulosRol && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#e0e0e0' }}>
                {rolSeleccionado?.icono} {rolSeleccionado?.label}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#546e7a' }}>
                {articulosRol.length} artículo{articulosRol.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Chip
              label="Ver todo el manual"
              size="small"
              onClick={limpiarFiltro}
              sx={{ cursor: 'pointer', color: 'var(--c-acento)', bgcolor: 'transparent',
                border: '1px solid var(--c-primario)', fontSize: 12,
                '&:hover': { bgcolor: 'rgba(144,202,249,0.06)' } }}
            />
            {ROL_SISTEMA_PARA_PDF[rolFiltro] && (
              <Button
                size="small"
                startIcon={generandoPdf ? <CircularProgress size={13} sx={{ color: '#90caf9' }} /> : <PictureAsPdfIcon sx={{ fontSize: 15 }} />}
                onClick={descargarPdf}
                disabled={generandoPdf}
                sx={{ ml: 'auto', fontSize: 12, color: '#90caf9', border: '1px solid rgba(144,202,249,0.25)',
                  textTransform: 'none', px: 1.5,
                  '&:hover': { borderColor: 'rgba(144,202,249,0.5)', bgcolor: 'rgba(144,202,249,0.06)' } }}
              >
                {generandoPdf ? 'Generando...' : 'Descargar manual en PDF'}
              </Button>
            )}
          </Box>
          <ListaArticulos articulos={articulosRol} navigate={navigate} />
        </Box>
      )}

      {/* Vista principal — grid de roles */}
      {!resultadosBusqueda && !rolFiltro && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
            {rolesVisibles.map(r => (
              <Box
                key={r.id}
                onClick={() => seleccionarRol(r.id)}
                sx={{
                  p: 2.5, borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.07)',
                  bgcolor: 'var(--c-superficie)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'var(--c-acento)', bgcolor: 'rgba(144,202,249,0.04)' },
                  transition: 'all 0.15s',
                }}
              >
                <Typography sx={{ fontSize: 28, mb: 1 }}>{r.icono}</Typography>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: 15, mb: 0.5 }}>
                  {r.label}
                </Typography>
                <Typography sx={{ color: '#546e7a', fontSize: 13 }}>
                  {r.descripcion}
                </Typography>
                <Typography sx={{ color: 'var(--c-acento)', fontSize: 12, mt: 1.5 }}>
                  {ARTICULOS.filter(a => a.rol === r.id).length} artículos →
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Links a Glosario y FAQ */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[
              { path: '/manual/glosario', icono: '📖', label: 'Glosario', desc: 'Significado de los términos del sistema' },
              { path: '/manual/faq',      icono: '❓', label: 'Preguntas frecuentes', desc: 'Respuestas a dudas comunes por rol' },
            ].map(item => (
              <Box
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  flex: '1 1 200px', p: 2, borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.07)',
                  bgcolor: 'var(--c-superficie)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  '&:hover': { borderColor: 'var(--c-acento)', bgcolor: 'rgba(144,202,249,0.04)' },
                  transition: 'all 0.15s',
                }}
              >
                <Typography sx={{ fontSize: 22 }}>{item.icono}</Typography>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: 14 }}>{item.label}</Typography>
                  <Typography sx={{ color: '#546e7a', fontSize: 12 }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function ListaArticulos({ articulos, navigate }) {
  if (!articulos.length) {
    return <Typography sx={{ color: '#546e7a', fontSize: 14 }}>No hay artículos disponibles.</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {articulos.map(a => (
        <Box
          key={a.id}
          onClick={() => navigate(`/manual/${a.id}`)}
          sx={{
            p: '12px 16px', borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.06)',
            bgcolor: 'var(--c-superficie)',
            cursor: 'pointer',
            '&:hover': { borderColor: 'var(--c-acento)', bgcolor: 'rgba(144,202,249,0.04)' },
            transition: 'all 0.15s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: 14, mb: 0.5 }}>
                {a.titulo}
              </Typography>
              <Typography sx={{ color: '#546e7a', fontSize: 13 }}>
                {a.resumen}
              </Typography>
            </Box>
            <Typography sx={{ color: '#3d5a6e', fontSize: 12, flexShrink: 0, mt: 0.25 }}>
              {a.tiempoLectura} min
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
